import React from 'react';
import {
    Grid,
    Typography,
    Box,
    Switch,
    TextField,
    CircularProgress,
    FormHelperText,
    Autocomplete
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { FeeScheduleSelection } from "../../../feeSchedulePage/feeScheduleSelectionComponent";
import StateDropdown from '../../RulesEditPage/components/StateDropdown';
import LocalityDropdown from '../../RulesEditPage/components/LocalityDropdown';
import GPCIDropdown from '../../RulesEditPage/components/GPCIDropdown';
import { GPCILocalityData } from '../../../../store/api/globalFeeSchedule';
import TruncatedText from './TruncatedText';

interface PaymentFeeScheduleMappingStepProps {
    values: any;
    touched: any;
    errors: any;
    classes: any;
    isCopyNewName: (key: string) => boolean;
    handleCopyModeToggle: (stepKey: string, index: number, checked: boolean, groupPath: string, group: any, setFieldValue: any) => void;
    setFieldValue: (field: string, value: any) => void;
    targetClient: any;
    handleTargetPaymentFeeScheduleChange: (setFieldValue: any, groupIndex: number, ruleIndex: number, obj: any, groupType: string) => void;
    handleTargetPaymentLabelChange: (setFieldValue: any, groupIndex: number, ruleIndex: number, obj: any, rule: any, groupType: string) => void;
    handleNewPaymentFeeScheduleChange: (setFieldValue: any, index: number, val: any, groupType: string) => void;
    handleBlur: (e: any) => void;
    getPaymentFeeScheduleHelperText: (touched: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    getPaymentFeeScheduleError: (errors: any, key: string, index: number, ruleIndex?: number, type?: string) => any;
    nameExistErrors: any;
    nameValidatePending: any;
    gpciLocalityData: GPCILocalityData[];
    feeScheduleLabels: any;
    fetchItemsBasedOnTargetClient: (rule: any) => void;
    feeScheduleLabelsValidate: any;
    getFeeScheduleLabelsFromTargetClientsPending: boolean;
}

const PaymentFeeScheduleMappingStep: React.FC<PaymentFeeScheduleMappingStepProps> = ({
    values,
    touched,
    errors,
    classes,
    isCopyNewName,
    handleCopyModeToggle,
    setFieldValue,
    targetClient,
    handleTargetPaymentFeeScheduleChange,
    handleTargetPaymentLabelChange,
    handleNewPaymentFeeScheduleChange,
    handleBlur,
    getPaymentFeeScheduleHelperText,
    getPaymentFeeScheduleError,
    nameExistErrors,
    nameValidatePending,
    gpciLocalityData,
    feeScheduleLabels,
    fetchItemsBasedOnTargetClient,
    feeScheduleLabelsValidate,
    getFeeScheduleLabelsFromTargetClientsPending
}) => {
    return (
        <>
            {values?.paymentFeeScheduleMapping?.localFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px" }}>Local Fee Schedules</Typography>}
                                            
            {values?.paymentFeeScheduleMapping?.localFeeSchedules?.map((group, index) => (
                <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                            <Box display="flex" alignItems="center" sx={{ ml: 4 }}>
                                <Typography variant="caption" sx={{ color: isCopyNewName(`step3-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: isCopyNewName(`step3-local-${index}`) ? 'normal' : 'bold', mr: 1,fontSize:13 }}>Select Fee Schedule At Target</Typography>
                                <Switch
                                    size="small"
                                    checked={isCopyNewName(`step3-local-${index}`)}
                                    onChange={(e) => handleCopyModeToggle('step3-local', index, e.target.checked, 'paymentFeeScheduleMapping.localFeeSchedules', group, setFieldValue)}
                                    color="primary"
                                />
                                <Typography variant="caption" sx={{ color: !isCopyNewName(`step3-local-${index}`) ? 'text.primary' : 'text.secondary', fontWeight: !isCopyNewName(`step3-local-${index}`) ? 'normal' : 'bold', ml: 1,fontSize:13 }}>Copy Fee Schedule From Source</Typography>
                            </Box>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {group.rules?.map((rule, ruleIndex) => (
                            <Grid container item xs={12} key={ruleIndex} spacing={2} sx={{ mb: 4, alignItems: 'start' }}>
                                {/* Left Column: Rule Info and Fee Schedule Selection */}
                                <Grid item xs={8}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingLeft: '20px' }}>
                                        <div style={{ minWidth: '250px' }}>
                                            <div className='font-12'><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></div>
                                            {rule.sourceLabel && <div className='font-12'><b>Rate Label : </b> <TruncatedText text={rule.sourceLabel} fixedWidth={180} /></div>}
                                        </div>
                                        {!isCopyNewName(`step3-local-${index}`) && (
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <FeeScheduleSelection
                                                    deletable={!!rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId?.id !== 0}
                                                    feeScheduleType='Local'
                                                    hideTypeToggle={true}
                                                    targetClient={targetClient?.schema}
                                                    defaultValue='Select Fee Schedule At Target'
                                                    handleSetFeeSchedule={(rowData) => handleTargetPaymentFeeScheduleChange(setFieldValue, index, ruleIndex, rowData, 'localFeeSchedules')}
                                                    value={rule.targetFeeScheduleId?.name}
                                                />
                                                {(getPaymentFeeScheduleHelperText(touched, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules') && getPaymentFeeScheduleError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')) && (
                                                    <FormHelperText error style={{ marginLeft: '14px' }}>
                                                        {getPaymentFeeScheduleError(errors, 'targetFeeScheduleId', index, ruleIndex, 'localFeeSchedules')}
                                                    </FormHelperText>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Grid>

                                {/* Right Column: New FS Name / Target Label / RBRVS */}
                                <Grid item xs={4}>
                                    {ruleIndex === 0 && isCopyNewName(`step3-local-${index}`) && (
                                        <Box sx={{ position: 'relative', mb: 2 }}>
                                            <TextField
                                                required
                                                label="New Fee Schedule Name"
                                                name={`paymentFeeScheduleMapping.localFeeSchedules.${index}.newFeeScheduleName`}
                                                className={classes.w90}
                                                value={group.newFeeScheduleName}
                                                onBlur={handleBlur}
                                                onChange={e => handleNewPaymentFeeScheduleChange(setFieldValue, index, e.target.value, 'localFeeSchedules')}
                                                helperText={getPaymentFeeScheduleError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') || nameExistErrors[`step3-localFeeSchedules-${index}`]}
                                                error={Boolean(getPaymentFeeScheduleError(errors, 'newFeeScheduleName', index, undefined, 'localFeeSchedules') || nameExistErrors[`step3-localFeeSchedules-${index}`])}
                                            />
                                            {nameValidatePending[`step3-localFeeSchedules-${index}`] === true ? (
                                                <Box className={classes.iconsContainer} sx={{ right: '12%' }}><CircularProgress size={10} /></Box>
                                            ) : nameValidatePending[`step3-localFeeSchedules-${index}`] === false && (
                                                <Box className={classes.iconsContainer} sx={{ right: '12%' }}>
                                                    {nameExistErrors[`step3-localFeeSchedules-${index}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {(!isCopyNewName(`step3-local-${index}`) && !!rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0) && (
                                        <Box sx={{ mb: 2, width: '90%' }}>
                                            <Autocomplete
                                                onChange={(e, value) => handleTargetPaymentLabelChange(setFieldValue, index, ruleIndex, value, rule, 'localFeeSchedules')}
                                                className={classes.w90}
                                                value={rule.targetLabelId}
                                                options={feeScheduleLabels[rule.targetFeeScheduleId.id] || []}
                                                onOpen={() => fetchItemsBasedOnTargetClient(rule)}
                                                noOptionsText={getFeeScheduleLabelsFromTargetClientsPending ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={10} /></Box> : 'No options'}
                                                getOptionLabel={(option) => option.label || option.name || ''}
                                                isOptionEqualToValue={(option, value) => (value?.label || value?.name) === (option.label || option.name)}
                                                renderInput={params => (
                                                    <Box style={{ position: 'relative' }}>
                                                        <TextField
                                                            {...params}
                                                            label="Target Rate Label"
                                                            required
                                                            fullWidth
                                                            helperText={getPaymentFeeScheduleError(errors, 'targetLabelId', index, ruleIndex, 'localFeeSchedules')}
                                                            error={Boolean(getPaymentFeeScheduleError(errors, 'targetLabelId', index, ruleIndex, 'localFeeSchedules'))}
                                                        />
                                                        {feeScheduleLabelsValidate[`pending-localFeeSchedules-${index}-${ruleIndex}`] === true ? (
                                                            <Box className={classes.iconsContainer}><CircularProgress size={10} /></Box>
                                                        ) : feeScheduleLabelsValidate[`pending-localFeeSchedules-${index}-${ruleIndex}`] === false && (
                                                            <Box className={classes.iconsContainer}>
                                                                {feeScheduleLabelsValidate[`done-localFeeSchedules-${index}-${ruleIndex}`] ? <CloseIcon className={classes.errorIcon} /> : <CheckIcon className={classes.successIcon} />}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                disableClearable
                                            />
                                        </Box>
                                    )}

                                    {(rule.isRBRVS || rule.isDME) && (
                                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                            <Box>
                                                <StateDropdown
                                                    scheduleId={group.sourceFeeScheduleId}
                                                    value={rule.selectedState}
                                                    onChange={(value) => {
                                                        setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                        if (value !== rule.selectedState) {
                                                            setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                            setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                        }
                                                    }}
                                                    onBlur={() => setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                                    error={getPaymentFeeScheduleError(errors, 'selectedState', index, ruleIndex, 'localFeeSchedules') || ""}
                                                    required={true}
                                                />
                                            </Box>
                                           {rule.isRBRVS && <Box>
                                                <GPCIDropdown
                                                    selectedState={rule.selectedState}
                                                    gpciLocalityData={gpciLocalityData}
                                                    value={rule.selectedGpci}
                                                    onChange={(value) => {
                                                        setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                        if (value !== rule.selectedGpci) {
                                                            setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                        }
                                                    }}
                                                    onBlur={() => setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                                    error={getPaymentFeeScheduleError(errors, 'selectedGpci', index, ruleIndex, 'localFeeSchedules') || ""}
                                                    required={true}
                                                />
                                            </Box>}
                                            {rule.isRBRVS && <Box>
                                                <LocalityDropdown
                                                    selectedState={rule.selectedState}
                                                    selectedGpci={rule.selectedGpci}
                                                    gpciLocalityData={gpciLocalityData}
                                                    value={rule.selectedLocality}
                                                    onChange={(value) => setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                                    onBlur={() => setFieldValue(`paymentFeeScheduleMapping.localFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                                    error={getPaymentFeeScheduleError(errors, 'selectedLocality', index, ruleIndex, 'localFeeSchedules') || ""}
                                                    required={true}
                                                />
                                            </Box>}
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}

            {values?.paymentFeeScheduleMapping?.globalFeeSchedules?.length > 0 && <Typography sx={{ fontSize: "20px", fontWeight: "bold", ml: "10px", mb: "20px",mt:"20px" }}>Global Fee Schedules</Typography>}
            {values?.paymentFeeScheduleMapping?.globalFeeSchedules?.map((group, index) => (
                <Grid container className='pb-4 align-items-start' key={index} sx={{ borderBottom: '1px solid #f0f0f0', pt: 2, px: 1 }}>
                    <Grid item xs={12} sx={{ mb: 1.5 }}>
                        <div style={{ marginTop: '8px' }}>
                            <span className='font-12'><b>Fee Schedule :</b> <TruncatedText text={group.feeScheduleName} fixedWidth={250} /></span>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        {group.rules?.map((rule, ruleIndex) => (
                            <Grid container item xs={12} key={ruleIndex} spacing={2} sx={{ mb: (rule.isRBRVS || rule.isDME) ? 8 : 4, alignItems: (rule.isRBRVS || rule.isDME) ? 'center' : 'start' }}>
                                {/* Left Column: Rule Info */}
                                <Grid item xs={(rule.isRBRVS || rule.isDME) ? 4 : 5}>
                                    <div style={{ paddingLeft: '20px' }}>
                                        <div className='font-12'><b>Rule :</b> <TruncatedText text={rule.ruleName} fixedWidth={180} /></div>
                                        {rule.sourceLabel && <div className='font-12'><b>Rate Label :</b> <TruncatedText text={rule.sourceLabel} fixedWidth={180} /></div>}
                                    </div>
                                </Grid>

                                 {/* Middle Column: Target Rate Label */}
                                <Grid item xs={(rule.isRBRVS || rule.isDME) ? 4 : 7}>
                                    {/* {(!!rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0) && (
                                        <Box sx={{ width: '90%' }}>
                                            <Autocomplete
                                                onChange={(e, value) => handleTargetPaymentLabelChange(setFieldValue, index, ruleIndex, value, rule, 'globalFeeSchedules')}
                                                className={classes.w90}
                                                value={rule.targetLabelId}
                                                options={feeScheduleLabels[rule.targetFeeScheduleId.id] || []}
                                                onOpen={() => fetchItemsBasedOnTargetClient(rule)}
                                                noOptionsText={getFeeScheduleLabelsFromTargetClientsPending ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={10} /></Box> : 'No options'}
                                                getOptionLabel={(option) => option.label || option.name || ''}
                                                isOptionEqualToValue={(option, value) => (value?.label || value?.name) === (option.label || option.name)}
                                                renderInput={params => (
                                                    <Box style={{ position: 'relative' }}>
                                                        <TextField
                                                            {...params}
                                                            label="Target Rate Label"
                                                            required
                                                            fullWidth
                                                            helperText={getPaymentFeeScheduleError(errors, 'targetLabelId', index, ruleIndex, 'globalFeeSchedules')}
                                                            error={Boolean(getPaymentFeeScheduleError(errors, 'targetLabelId', index, ruleIndex, 'globalFeeSchedules'))}
                                                        />
                                                        {feeScheduleLabelsValidate[`pending-globalFeeSchedules-${index}-${ruleIndex}`] === true ? (
                                                            <Box className={classes.iconsContainer}><CircularProgress size={10} /></Box>
                                                        ) : feeScheduleLabelsValidate[`pending-globalFeeSchedules-${index}-${ruleIndex}`] === false && (
                                                            <Box className={classes.iconsContainer}>
                                                                {feeScheduleLabelsValidate[`done-globalFeeSchedules-${index}-${ruleIndex}`] ? <CheckIcon className={classes.successIcon} /> : <CloseIcon className={classes.errorIcon} />}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                                disableClearable
                                            />
                                        </Box>
                                    )} */}
                                </Grid>

                                {/* Right Column: Regional Config (if RBRVS) */}
                                {(rule.isRBRVS || rule.isDME) && (
                                    <Grid item xs={4}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '90%' }}>
                                            <Box>
                                        <StateDropdown
                                            scheduleId={group.sourceFeeScheduleId}
                                            value={rule.selectedState}
                                            onChange={(value) => {
                                                setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, value);
                                                if (value !== rule.selectedState) {
                                                    setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, null);
                                                    setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                }
                                            }}
                                            onBlur={() => setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedState`, rule.selectedState || '')}
                                            error={getPaymentFeeScheduleError(errors, 'selectedState', index, ruleIndex, 'globalFeeSchedules') || ""}
                                            required={true}
                                        />
                                    </Box>
                                   {rule.isRBRVS && <Box>
                                        <GPCIDropdown
                                            selectedState={rule.selectedState}
                                            gpciLocalityData={gpciLocalityData}
                                            value={rule.selectedGpci}
                                            onChange={(value) => {
                                                setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, value);
                                                if (value !== rule.selectedGpci) {
                                                    setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, null);
                                                }
                                            }}
                                            onBlur={() => setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedGpci`, rule.selectedGpci || '')}
                                            error={getPaymentFeeScheduleError(errors, 'selectedGpci', index, ruleIndex, 'globalFeeSchedules') || ""}
                                            required={true}
                                        />
                                    </Box>}
                                    {rule.isRBRVS && <Box>
                                        <LocalityDropdown
                                            selectedState={rule.selectedState}
                                            selectedGpci={rule.selectedGpci}
                                            gpciLocalityData={gpciLocalityData}
                                            value={rule.selectedLocality}
                                            onChange={(value) => setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, value)}
                                            onBlur={() => setFieldValue(`paymentFeeScheduleMapping.globalFeeSchedules.${index}.rules.${ruleIndex}.selectedLocality`, rule.selectedLocality || '')}
                                            error={getPaymentFeeScheduleError(errors, 'selectedLocality', index, ruleIndex, 'globalFeeSchedules') || ""}
                                            required={true}
                                        />
                                    </Box>}
                                        </div>
                                    </Grid>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            ))}

        </>
    );
};

export default PaymentFeeScheduleMappingStep;
